import { useEffect } from "react";

const setOrCreateMeta = (key, value, attr = "name") => {
  if (!value) return;
  let tag = document.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", value);
};

const setOrCreateLink = (rel, href) => {
  if (!href) return;
  let tag = document.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  let tag = document.querySelector(`script[data-jsonld="${id}"]`);
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.setAttribute("data-jsonld", id);
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
};

/**
 * Set <title>, meta description, OG tags, canonical, optional JSON-LD schema.
 */
export default function useSeo({
  title,
  description,
  image,
  canonicalPath,
  ogType = "website",
  jsonLd = null,
} = {}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setOrCreateMeta("description", description);
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
    const canonical = baseUrl + (canonicalPath || window.location.pathname);
    setOrCreateLink("canonical", canonical);
    setOrCreateMeta("og:title", title, "property");
    setOrCreateMeta("og:description", description, "property");
    setOrCreateMeta("og:type", ogType, "property");
    setOrCreateMeta("og:url", canonical, "property");
    if (image) setOrCreateMeta("og:image", image, "property");
    setOrCreateMeta("twitter:card", image ? "summary_large_image" : "summary");
    setOrCreateMeta("twitter:title", title);
    setOrCreateMeta("twitter:description", description);
    if (image) setOrCreateMeta("twitter:image", image);
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((d, i) => setJsonLd(`page-${i}`, d));
    }
    // hreflang alternates for TR/EN/DE
    ["tr", "en", "de"].forEach((lng) => {
      let tag = document.querySelector(`link[rel="alternate"][hreflang="${lng}"]`);
      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "alternate");
        tag.setAttribute("hreflang", lng);
        document.head.appendChild(tag);
      }
      tag.setAttribute("href", canonical);
    });
  }, [title, description, image, canonicalPath, ogType, jsonLd]);
}

/** Organization + ConsultingService schema for homepage */
export const getOrganizationSchema = (baseUrl) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Otel Pro",
  url: baseUrl,
  logo: "https://customer-assets.emergentagent.com/job_otel-yerlesim/artifacts/izskkfcr_otel-pro-logo-transparent.png",
  description: "Otel açılışında uzman danışmanlık ve premium B2B ekipman tedariki.",
  address: { "@type": "PostalAddress", addressCountry: "TR" },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+90-532-737-13-06",
    contactType: "sales",
    areaServed: "TR",
    availableLanguage: ["tr", "en", "de"],
  },
});

export const getConsultingServiceSchema = (baseUrl) => ({
  "@context": "https://schema.org",
  "@type": "ConsultingService",
  name: "Otel Pro Açılış Danışmanlığı",
  url: baseUrl,
  serviceType: "Hotel Opening Consultancy & Equipment Supply",
  provider: { "@type": "Organization", name: "Otel Pro" },
  areaServed: { "@type": "Country", name: "Türkiye" },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Otel Ekipman Kategorileri",
  },
});

export const getProductSchema = (product, baseUrl) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: product.name,
  description: product.short_description,
  image: product.images?.[0],
  sku: product.slug,
  brand: { "@type": "Brand", name: "Otel Pro" },
  offers: {
    "@type": "Offer",
    priceCurrency: "TRY",
    price: "0",
    availability: "https://schema.org/InStock",
    url: `${baseUrl}/urun/${product.slug}`,
    priceValidUntil: new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString().slice(0, 10),
  },
});

export const getArticleSchema = (post, baseUrl) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title_tr,
  description: post.excerpt,
  image: post.cover_image,
  author: { "@type": "Organization", name: post.author || "Otel Pro" },
  publisher: { "@type": "Organization", name: "Otel Pro" },
  datePublished: post.published_at,
  mainEntityOfPage: `${baseUrl}/blog/${post.slug}`,
});
